<script setup lang="ts">
import { computed, ref } from 'vue';
import { useDropZone, useFileDialog } from '@vueuse/core';

import AttachmentsCustomTable from '@/components/customTables/AttachmentsCustomTable.vue';
import DropZoneArea from '@/components/dropZones/DropZoneArea.vue';
import DropZoneButton from '@/components/dropZones/DropZoneButton.vue';
import useField from '@/composables/useField';

export interface Props {
  modelValue?: File[];
  disabled?: boolean;
  limit?: number;
  variant?: 'button' | 'dropzone';
  supportedFormats?: string[];
  maxFileSizeMb?: number;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  disabled: false,
  limit: 0,
  variant: 'button',
  supportedFormats: () => ['pdf', 'png', 'jpg', 'jpeg'],
  maxFileSizeMb: 25,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref<File[]>(props.modelValue);
const validationErrors = ref<string[]>([]);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});

const dropZoneRef = ref<HTMLDivElement>();

const normalizedSupportedFormats = computed(() => {
  return props.supportedFormats.map((format) =>
    format.trim().toLowerCase().replace(/^\./, ''),
  );
});

const maxFileSizeBytes = computed(() => {
  return props.maxFileSizeMb * 1024 * 1024;
});

const acceptedExtensions = computed(() => {
  return normalizedSupportedFormats.value
    .map((format) => `.${format}`)
    .join(',');
});

const isLimitOrMore = computed(() => {
  return !!(props.limit && fieldValue.value.length >= props.limit);
});

function getFileExtension(file: File): string {
  const fileNameParts = file.name.split('.');
  if (fileNameParts.length < 2) return '';
  return fileNameParts[fileNameParts.length - 1].toLowerCase();
}

function isFileFormatAllowed(file: File): boolean {
  if (!normalizedSupportedFormats.value.length) return true;
  return normalizedSupportedFormats.value.includes(getFileExtension(file));
}

function isFileSizeAllowed(file: File): boolean {
  return file.size <= maxFileSizeBytes.value;
}

function setValidationErrors(messages: string[]): void {
  validationErrors.value = messages;
}

function addFiles(files: File[]): void {
  if (props.disabled || !files.length) return;

  const nextErrors: string[] = [];

  const formatValidFiles = files.filter((file) => isFileFormatAllowed(file));
  const invalidFormatCount = files.length - formatValidFiles.length;
  if (invalidFormatCount > 0) {
    nextErrors.push('Some files have unsupported format');
  }

  const validFiles = formatValidFiles.filter((file) => isFileSizeAllowed(file));
  const invalidSizeCount = formatValidFiles.length - validFiles.length;
  if (invalidSizeCount > 0) {
    nextErrors.push(`Some files exceed ${props.maxFileSizeMb}MB`);
  }

  let filesToAdd = validFiles;

  if (props.limit > 0) {
    const availableSlots = Math.max(props.limit - fieldValue.value.length, 0);
    if (availableSlots === 0) {
      nextErrors.push('The files limit is reached');
      setValidationErrors(nextErrors);
      return;
    }

    if (filesToAdd.length > availableSlots) {
      filesToAdd = filesToAdd.slice(0, availableSlots);
      nextErrors.push(
        `Only ${availableSlots} file${availableSlots === 1 ? '' : 's'} can be added`,
      );
    }
  }

  setValidationErrors(nextErrors);

  if (!filesToAdd.length) {
    return;
  }

  fieldValue.value = [...fieldValue.value, ...filesToAdd];
}

function onDrop(files: File[] | null): void {
  if (!files) return;
  addFiles(files);
}

const isMultiple = computed(() => {
  return props.limit - fieldValue.value.length !== 1;
});

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  multiple: isMultiple.value, // TODO make it reactive
  preventDefaultForUnhandled: false,
});

const { /* files, */ open, /* reset, */ onCancel, onChange } = useFileDialog({
  multiple: isMultiple.value, // TODO make it reactive
  directory: false,
  accept: acceptedExtensions.value,
});

onChange((files: FileList | null) => {
  if (!files) return;
  addFiles([...files]);
});

/* eslint-disable-next-line @typescript-eslint/no-empty-function */
onCancel(() => {});

function deleteFiles(index: number): void {
  fieldValue.value.splice(index, 1);
  fieldValue.value = [...fieldValue.value];
}
</script>

<template>
  <div
    :class="$style.detailedJobAttachmentsValidatedDropZoneField"
    data-testid="detailedJobAttachmentsValidatedDropZoneField"
  >
    <div v-if="props.limit" class="mt-n1 mb-3">
      Attachments,
      <span :class="$style.limitText">
        {{ props.limit }}
        <template v-if="props.limit !== 1">files</template>
        <template v-else>file</template>
        limit
      </span>
    </div>
    <div ref="dropZoneRef">
      <DropZoneButton
        v-if="props.variant === 'button'"
        :is-over="isOverDropZone"
        :is-disabled="isLimitOrMore || props.disabled"
        :is-multiple="isMultiple"
        @open="open"
      />
      <DropZoneArea
        v-else
        :is-over="isOverDropZone"
        :is-disabled="isLimitOrMore || props.disabled"
        :supported-formats="props.supportedFormats"
        :max-file-size-mb="props.maxFileSizeMb"
        @open="open"
      />
    </div>
    <AttachmentsCustomTable
      v-if="fieldValue.length"
      :attachment-files="fieldValue"
      :delete-files="deleteFiles"
      :class="$style.attachmentsTable"
      class="mt-2"
    />
    <div
      v-if="props.limit && fieldValue.length > props.limit"
      class="v-input--error v-text-field"
    >
      <div class="v-input__details">
        <div class="v-messages">
          <div class="v-messages__message">
            The files limit is exceeded, please remove some files from the list
          </div>
        </div>
      </div>
    </div>
    <div v-if="validationErrors.length" class="v-input--error v-text-field">
      <div class="v-input__details">
        <div class="v-messages">
          <div
            v-for="(error, index) in validationErrors"
            :key="`validation-error-${index}`"
            class="v-messages__message"
          >
            {{ error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobAttachmentsValidatedDropZoneField {
  color: colors.$color-text-secondary;
  line-height: 1.4;
}
  .limitText {
    color: colors.$color-text-tertiary;
  }
  .attachmentsTable {
    width: 100%;
  }
</style>
